import React from 'react';
import { Link } from 'react-router-dom';
import { LeaveDto, UserYearlyLeaveSummary } from '../../../../../types/leaves.types';
import Table from '../../../../shared/dataTable/index';
import { LEAVE_STATUSES } from '../../../../../constants/leaveConstants';
import { PermissionType, User } from '../../../../../types/user.types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';

interface LeaveHistoryProps {
    leaves: LeaveDto[];
    formatDate: (date: string | Date | undefined) => string;
    leaveSummary: UserYearlyLeaveSummary;
    onCreateLeave?: () => void;
    userData: User;
    startDateLeave: string;
    endDateLeave: string;
}

const LeaveHistory: React.FC<LeaveHistoryProps> = ({
    leaves,
    leaveSummary,
    onCreateLeave,
    userData,
}) => {
    const { t } = useTranslation();
    const { user } = useSelector((state: RootState) => state.auth);

    const isResigned =
        userData?.status?.toString().trim().toLowerCase() === 'resigned';

    const totalAnnualLeaveLimit = Number(
        leaveSummary?.totalAnnualLeaveLimit ?? 0,
    );
    const usedAnnualLeaveDays = Number(
        leaveSummary?.usedAnnualLeaveDays ?? 0,
    );
    const remainingAnnualLeaveDays = Number(leaveSummary?.remainingAnnualLeaveDays ?? 0);

    const usedPercentage = totalAnnualLeaveLimit > 0
        ? Math.min((usedAnnualLeaveDays / totalAnnualLeaveLimit) * 100, 100)
        : 0;

    const remainingPercentage = totalAnnualLeaveLimit > 0
        ? Math.min((remainingAnnualLeaveDays / totalAnnualLeaveLimit) * 100, 100)
        : 0;

    const canCreateLeave =
        !isResigned &&
        Boolean(
            user?.permissions?.includes(PermissionType.EDIT_LEAVE),
        );

    const leaveColumns = [
        {
            title: t('employeesDetail.leaves.type'),
            dataIndex: 'type',
            render: (_text: string, record: LeaveDto) => (
                <div className="d-flex align-items-center">
                    <p className="fs-14 fw-medium d-flex align-items-center mb-0">
                        {t(`leaveTypes.${record.type}`)}
                    </p>
                </div>
            ),
            sorter: (a: LeaveDto, b: LeaveDto) =>
                String(a.type).localeCompare(String(b.type)),
        },
        {
            title: t('employeesDetail.leaves.date'),
            dataIndex: 'startDate',
            render: (_text: string, record: LeaveDto) => (
                <div className="d-flex align-items-center">
                    <p className="fs-14 fw-medium d-flex align-items-center mb-0">
                        {record.startDate?.split('T')[0] ?? '-'}
                    </p>
                </div>
            ),
            sorter: (a: LeaveDto, b: LeaveDto) =>
                String(a.startDate).localeCompare(String(b.startDate)),
        },
        {
            title: 'Durum',
            dataIndex: 'status',
            render: (_text: string, record: LeaveDto) => (
                <div className="d-flex align-items-center">
                    <p className="fs-14 fw-medium d-flex align-items-center mb-0">
                        {LEAVE_STATUSES[
                            record.status as keyof typeof LEAVE_STATUSES
                        ] ?? record.status}
                    </p>
                </div>
            ),
            sorter: (a: LeaveDto, b: LeaveDto) =>
                String(a.status).localeCompare(String(b.status)),
        },
        {
            title: t('employeesDetail.leaves.description'),
            dataIndex: 'reason',
            render: (_text: string, record: LeaveDto) => (
                <div className="d-flex align-items-center">
                    <p
                        className="fs-14 fw-medium d-flex align-items-center mb-0 truncate max-w-xs overflow-ellipsis overflow-hidden whitespace-nowrap"
                        style={{ maxWidth: '200px' }}
                        title={record.reason}
                    >
                        {record.reason || '-'}
                    </p>
                </div>
            ),
            sorter: (a: LeaveDto, b: LeaveDto) =>
                String(a.reason ?? '').localeCompare(String(b.reason ?? '')),
        },
    ];

    return (
        <div className="accordion-item">
            <div className="accordion-header" id="headingTwo">
                <div
                    className="accordion-button collapsed"
                    data-bs-toggle="collapse"
                    data-bs-target="#primaryBorderTwo"
                    aria-expanded="false"
                    aria-controls="primaryBorderTwo"
                    style={{ cursor: 'pointer' }}
                >
                    <div className="d-flex align-items-center justify-content-between flex-fill">
                        <h5>{t('employeesDetail.leaves.title')}</h5>
                        <div className="d-flex align-items-center">
                            <i className="ti ti-chevron-down fs-18" />
                        </div>
                    </div>
                </div>
            </div>

            <div
                id="primaryBorderTwo"
                className="accordion-collapse collapse border-top"
                aria-labelledby="headingTwo"
                data-bs-parent="#accordionExample"
            >
                <div className="accordion-body">
                    {isResigned && (
                        <div
                            className="alert alert-warning d-flex align-items-start mb-3"
                            role="alert"
                        >
                            <i className="ti ti-user-off fs-20 me-2 mt-1" />
                            <div>
                                <div className="small mt-1">
                                    Geçmiş izin kayıtları görüntülenebilir ancak yeni
                                    izin oluşturulamaz ve kalan izin bakiyesi
                                    kullanılamaz.
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-9">
                            <div className="card h-100">
                                <div className="card-body text-center">
                                    <div className="row">
                                        <div className="col-4">
                                            <p className="mb-1">
                                                <i className="ti ti-check-circle text-primary me-1" />
                                                {t(
                                                    'employeesDetail.leaves.totalAnnualLeaveLimit',
                                                )}
                                            </p>
                                            <h4>
                                                {totalAnnualLeaveLimit}{' '}
                                                {t('employeesDetail.leaves.days')}
                                            </h4>
                                        </div>

                                        <div className="col-4">
                                            <p className="mb-1">
                                                <i className="ti ti-calendar text-danger me-1" />
                                                {t(
                                                    'employeesDetail.leaves.usedAnnualLeaveDays',
                                                )}
                                            </p>
                                            <h4>
                                                {usedAnnualLeaveDays}{' '}
                                                {t('employeesDetail.leaves.days')}
                                            </h4>
                                        </div>

                                        <div className="col-4">
                                            <p className="mb-1">
                                                <i className="ti ti-clock text-success me-1" />
                                                {isResigned
                                                    ? 'Kullanılabilir İzin Günü'
                                                    : t(
                                                        'employeesDetail.leaves.remainingAnnualLeaveDays',
                                                    )}
                                            </p>
                                            <h4>
                                                {isResigned
                                                    ? 0
                                                    : remainingAnnualLeaveDays}{' '}
                                                {t('employeesDetail.leaves.days')}
                                            </h4>

                                            {isResigned && (
                                                <small className="text-muted d-block">
                                                    Ayrılış kaydındaki bakiye:{' '}
                                                    {remainingAnnualLeaveDays}{' '}
                                                    {t('employeesDetail.leaves.days')}
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        className="progress mt-3"
                                        style={{ height: 8 }}
                                    >
                                        {isResigned ? (
                                            <div
                                                className="progress-bar bg-secondary"
                                                role="progressbar"
                                                style={{ width: '100%' }}
                                                aria-valuenow={100}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            />
                                        ) : (
                                            <>
                                                <div
                                                    className="progress-bar bg-danger"
                                                    role="progressbar"
                                                    style={{ width: `${usedPercentage}%` }}
                                                    aria-valuenow={usedPercentage}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                />
                                                <div
                                                    className="progress-bar bg-success"
                                                    role="progressbar"
                                                    style={{ width: `${remainingPercentage}%` }}
                                                    aria-valuenow={remainingPercentage}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-3">
                            <div className="card h-100">
                                <div className="card-body d-flex align-items-center justify-content-center">
                                    <div>
                                        <p className="mb-1">
                                            <i className="ti ti-check-circle text-primary me-1" />
                                            {t(
                                                'employeesDetail.leaves.usedMedicalLeaves',
                                            )}
                                        </p>
                                        <h4 className="text-center">
                                            {leaveSummary?.approvedMedicalLeaves ?? 0}{' '}
                                            {t('employeesDetail.leaves.days')}
                                        </h4>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {canCreateLeave && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary w-25 mt-3"
                                onClick={onCreateLeave}
                            >
                                İzin Oluştur
                            </button>
                        )}

                        <div className="card-header">
                            <h5 className="card-title mb-0 mt-3">
                                {isResigned
                                    ? `${userData?.firstName} ${userData?.lastName} için seçili dönemde ${leaves?.length ?? 0} adet izin kaydı bulunuyor.`
                                    : `${userData?.firstName} ${userData?.lastName} bu ay ${leaves?.length ?? 0} adet izin isteği oluşturdu.`}
                            </h5>
                        </div>

                        <div className="card-body p-0">
                            <Table
                                dataSource={leaves || []}
                                columns={leaveColumns}
                                Selection={false}
                            />
                        </div>
                    </div>
                </div>

                <div className="text-center" style={{ marginBottom: '20px' }}>
                    <Link to={`/user-leaves?user=${userData.id}`}>
                        {t('employeesDetail.leaves.viewAll')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LeaveHistory;